import { useCallback, useEffect, useRef, useState } from 'react';

export const CATALOG_SAVE_DEBOUNCE_MS = 1500;

async function postCatalogFullSync({ sessionToken, getHeaders, categories, products }) {
  const tok = String(sessionToken || '').trim();
  const response = await fetch('/api/admin_catalog.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders() },
    body: JSON.stringify({
      session_token: tok,
      action: 'full_sync',
      categories,
      products,
    }),
  });
  const text = await response.text();
  let body = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    const preview = text.slice(0, 140).replace(/\s+/g, ' ');
    if (/Proxy error|Could not proxy|ECONNREFUSED|ECONNRESET/i.test(text)) {
      throw new Error(
        `Could not reach PHP (HTTP ${response.status}). Run npm run php:serve, then retry. See LOCAL_DEV.md.`
      );
    }
    throw new Error(
      `Server response was not JSON (HTTP ${response.status}). Run npm run php:serve and npm start. Preview: ${preview}`
    );
  }
  if (!response.ok || !body.success) {
    throw new Error(body.error || `HTTP ${response.status}`);
  }
  return body;
}

/**
 * Hybrid catalog persist: debounced save for text edits, immediate save for discrete actions.
 */
export function useCatalogPersist({
  sessionToken,
  catalogDbActive,
  editableCategories,
  editableProducts,
  refetch,
  navigate,
  getHeaders,
  loading,
  setStatus,
}) {
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveTick, setSaveTick] = useState(0);

  const draftRef = useRef({ categories: [], products: {} });
  const pendingSnapshotRef = useRef(null);
  const saveDelayRef = useRef(CATALOG_SAVE_DEBOUNCE_MS);
  const inFlightRef = useRef(false);
  const queuedRef = useRef(false);
  const skipAutoSaveRef = useRef(false);

  useEffect(() => {
    if (pendingSnapshotRef.current) return;
    draftRef.current = { categories: editableCategories, products: editableProducts };
  }, [editableCategories, editableProducts]);

  const persistCatalog = useCallback(
    async ({ silent = false } = {}) => {
      const tok = String(sessionToken || '').trim();
      if (!tok) {
        if (!silent) {
          setStatus('Sign in again.');
          navigate(`/login?next=${encodeURIComponent('/admin')}`);
        }
        return { ok: false, error: 'Not signed in' };
      }
      if (!catalogDbActive) {
        return {
          ok: false,
          error: 'MySQL catalog is not connected. Run npm run php:serve or use the live site admin.',
        };
      }

      if (inFlightRef.current) {
        queuedRef.current = true;
        return { ok: true, queued: true };
      }

      inFlightRef.current = true;
      setSaving(true);
      if (!silent) {
        setStatus('Saving catalog to MySQL…');
      }

      const snapshot = pendingSnapshotRef.current;
      pendingSnapshotRef.current = null;
      const { categories, products } = snapshot ?? draftRef.current;

      try {
        const body = await postCatalogFullSync({
          sessionToken: tok,
          getHeaders,
          categories,
          products,
        });
        setDirty(false);
        skipAutoSaveRef.current = true;
        await refetch();
        skipAutoSaveRef.current = false;
        if (!silent) {
          setStatus(
            body.warning ? `Catalog saved. ${body.warning}` : 'Catalog saved. Shop is up to date.'
          );
        } else if (body.warning) {
          setStatus(body.warning);
        }
        return { ok: true, warning: body.warning };
      } catch (err) {
        setDirty(true);
        const msg = err?.message || 'Could not save catalog.';
        if (!silent) {
          setStatus(`Save failed: ${msg}`);
        }
        return { ok: false, error: msg };
      } finally {
        inFlightRef.current = false;
        setSaving(false);
        if (queuedRef.current) {
          queuedRef.current = false;
          await persistCatalog({ silent: true });
        }
      }
    },
    [sessionToken, catalogDbActive, getHeaders, refetch, navigate, setStatus]
  );

  const queueCatalogSave = useCallback(
    (delay = CATALOG_SAVE_DEBOUNCE_MS, snapshot = null) => {
      if (loading || skipAutoSaveRef.current) return;
      if (snapshot?.categories && snapshot?.products) {
        pendingSnapshotRef.current = snapshot;
        draftRef.current = snapshot;
      }
      setDirty(true);
      saveDelayRef.current = Math.max(0, delay);
      setSaveTick((n) => n + 1);
    },
    [loading]
  );

  const clearDirty = useCallback(() => {
    setDirty(false);
    setSaveTick(0);
  }, []);

  useEffect(() => {
    if (saveTick === 0 || loading) return undefined;

    const delay = saveDelayRef.current;
      const id = setTimeout(async () => {
      if (!catalogDbActive) {
        setStatus(
          'Changes kept in this browser. Connect MySQL (npm run php:serve) or use Publish catalog when ready.'
        );
        return;
      }
      const result = await persistCatalog({ silent: delay > 0 });
      if (result.ok && delay > 0) {
        setStatus('Catalog saved.');
      }
    }, delay);

    return () => clearTimeout(id);
  }, [saveTick, editableCategories, editableProducts, loading, catalogDbActive, persistCatalog, setStatus]);

  const publishCatalog = useCallback(async () => {
    await persistCatalog({ silent: false });
  }, [persistCatalog]);

  return {
    dirty,
    saving,
    queueCatalogSave,
    publishCatalog,
    clearDirty,
    persistCatalog,
  };
}
